import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';
import { Table, SelectOption } from 'component-library-and-tools';
import { useSelector } from 'react-redux';

import SearchBar from 'shared/components/SearchBar';
import MultiSelect from 'shared/components/MultiSelect';
import { getDestinationTypes } from 'services/destination/getters';
import { DestinationType } from 'shared/types/destination';
import { IndexableObjectType } from 'shared/types/common';
import { getThemeMode } from 'services/user/getters';
import { colors, colorsByTheme, Theme } from 'lib/prisma-styles';

import DestinationSelectionCell from '../../../common/steps/DestinationSelectionCell';
import DestinationNameCell from '../../../common/steps/DestinationNameCell';
import { SELECT_TYPE_OPTIONS } from '../../../common/steps/DestinationSelection/constants';
import { DestinationSelectionProps } from './types';
import {
  SEARCH_FILTER_NAME,
  SELECT_FILTER_NAME,
} from './constants';

const SEARCH_INPUT_TEXT_CLASS_NAME = 'destination__search-input-text';
const SEARCH_ICON_CLASS_NAME = 'destination__search-icon';
const TABLE_HEADER_ROW_CLASS_NAME = 'destination__table-header-row';
const TABLE_HEADER_CELL_CLASS_NAME = 'destination__table-header-cell';
const TABLE_ROW_CLASS_NAME = 'destination__table-row';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const StyledFilterContainer = styled.div`
  display: flex;
  align-items: center;
  height: 3.666666667rem;
  margin-bottom: 1.333333333rem;
`;

const StyledSearchInput = styled(SearchBar)`
  flex-basis: 87.70%;
  height: 100%;
  margin-right: 1rem;
  font-size: 1.166666667rem;
`;

const StyledTypeMultiSelect = styled(MultiSelect)`
  flex-basis: 12.30%;
  height: 100%;
  font-size: 1.166666667rem;
`;

const StyledTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const StyledTable = styled(Table)`
  && {
    min-height: 0;
    font-size: 1.166666667rem;

    .${TABLE_HEADER_ROW_CLASS_NAME} {
      border-color: #8d9199;
    }

    .${TABLE_ROW_CLASS_NAME} {
      border-bottom-color: ${colors.interactive.defaultBorder};
      cursor: pointer;
    }
  }
`;

const filterByDestinationName = (
  destinationTypes: DestinationType[],
  destinationName: string,
) => (
  destinationTypes.filter((destinationType) => (
    new RegExp(destinationName, 'ig').test(destinationType || ''))
  )
);

const filterByDestinationTypes = (
  destinationTypes: DestinationType[],
  selectedDestinationTypes: SelectOption[],
) => {
  if (isEmpty(selectedDestinationTypes)) {
    return destinationTypes;
  }

  return destinationTypes.filter((destination) => (
    !!selectedDestinationTypes.find(({ name }) => name === destination))
  );
};

const FILTER_MAPPER: IndexableObjectType = {
  [SEARCH_FILTER_NAME]: filterByDestinationName,
  [SELECT_FILTER_NAME]: filterByDestinationTypes,
};

const filterDestinations = (destinationTypes: DestinationType[], filters: Object) => {
  let filteredDestinations: DestinationType[] = destinationTypes;

  Object.entries(filters).forEach(([key, value]) => {
    const filterFn = FILTER_MAPPER[key];

    filteredDestinations = filterFn(filteredDestinations, value);
  });

  return filteredDestinations;
};

const DestinationSelection = ({
  onChange,
  values: selectedDestination,
}: DestinationSelectionProps) => {
  const destinationTypes = getDestinationTypes();
  const [filters, setFilters] = useState({});
  const theme: keyof Theme = useSelector(getThemeMode);

  const onSearch = useCallback((searchValue) => {
    setFilters({
      ...filters,
      [SEARCH_FILTER_NAME]: searchValue,
    });
  }, [filters]);

  const onSelect = useCallback((selectedTypes) => {
    setFilters({
      ...filters,
      [SELECT_FILTER_NAME]: selectedTypes,
    });
  }, [filters]);

  const onRowClick = useCallback((destinationType) => {
    onChange({
      ...selectedDestination,
      type: destinationType,
    });
  }, [onChange]);

  const filteredDestinations = filterDestinations(destinationTypes, filters);

  return (
    <StyledContainer>
      <StyledFilterContainer>
        <StyledSearchInput
          inputClassName={SEARCH_INPUT_TEXT_CLASS_NAME}
          iconClassName={SEARCH_ICON_CLASS_NAME}
          placeholder='Search destinations...'
          onSearch={onSearch}
        />
        <StyledTypeMultiSelect
          menuGap={4}
          options={SELECT_TYPE_OPTIONS}
          placeholder='Select type'
          allSelectedLabel='Type: All'
          onSelect={onSelect}
        />
      </StyledFilterContainer>
      <StyledTableContainer>
        <StyledTable
          headerRowClassName={TABLE_HEADER_ROW_CLASS_NAME}
          headerCellClassName={TABLE_HEADER_CELL_CLASS_NAME}
          rowClassName={TABLE_ROW_CLASS_NAME}
          // NOTE: This is temporary until we refactor the table to allow custom rows.
          buildBodyRowStyles={(destinationType) => {
            return destinationType === selectedDestination.type
              ? { backgroundColor: colorsByTheme[theme].interactive.selectedActive }
              : undefined
          }}
          rowData={filteredDestinations}
          columns={[
            {
              name: 'destinationSelection',
              headerLabel: '',
              dataAccessor: (destinationType) => destinationType,
              cellComponent: DestinationSelectionCell,
              cellComponentExtraProps: { selectedDestination },
              width: '4.32%',
              style: { maxWidth: 48 },
            },
            {
              name: 'destinationName',
              dataAccessor: (destinationType) => destinationType,
              cellComponent: DestinationNameCell,
              width: '95.68%',
            },
          ]}
          onRowClick={onRowClick}
          noDataMessage='No search results.'
        />
      </StyledTableContainer>
    </StyledContainer>
  );
};

export default DestinationSelection;
